import React from "react";
//#region types
import { FilePreviewsGridListItemProps } from "models/utilTypes/fileUtilsTypes";
import { FileEnums } from "models/enums";
import { isHeicOrHeif } from "utils/helpers/heicToJpegHelper";
//#endregion
//#region components
import { Grid, Skeleton } from "@mui/material";
import useMountedState from "../useMountedState";
import { Buffer } from "buffer";
//#endregion
import NoImage from "images/no_image.png";
import cssExports from "theme/styles/main.module.scss";

const FilesGridItemImage: React.FC<FilePreviewsGridListItemProps> = (props) => {
  const { getMounted } = useMountedState();
  const [imageContent, setImageContent] = React.useState<string | undefined>(undefined);
  const [isConverting, setIsConverting] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const loadAndConvertImage = async () => {
      if (props.fileMeta) {
        let base64Data: string | undefined;
        const contentType: string | undefined = props.fileMeta.contentType;

        if (props.fileMeta.content?.length > 0) {
          base64Data = props.fileMeta.content;
        } else {
          try {
            const res = await props.getImageContent(props.id);
            if (res != null) {
              base64Data = Buffer.from(res).toString();
            }
          } catch (e) {
            if (!cancelled && getMounted()) {
              setImageContent(NoImage);
            }
            return;
          }
        }

        if (cancelled) {
          return;
        }

        if (base64Data && contentType) {
          // Check if HEIC/HEIF conversion is needed
          if (isHeicOrHeif(contentType)) {
            if (!cancelled && getMounted()) {
              setIsConverting(true);
            }
            try {
              const { convertHeicToJpeg } = await import("utils/helpers/heicToJpegHelper");
              const convertedData = await convertHeicToJpeg(base64Data);
              if (!cancelled && getMounted()) {
                setImageContent(`data:${FileEnums.ContentTypes.ImageJPEG};base64,${convertedData}`);
                setIsConverting(false);
              }
            } catch (error) {
              console.error("Failed to convert HEIC image:", error);
              if (!cancelled && getMounted()) {
                setImageContent(NoImage);
                setIsConverting(false);
              }
            }
          } else {
            // Not HEIC, use original image
            if (!cancelled && getMounted()) {
              setImageContent(`data:${contentType};base64,${base64Data}`);
            }
          }
        }
      }
    };

    loadAndConvertImage();

    return () => {
      cancelled = true;
    };
  }, [props.fileMeta, props.id, getMounted, props.getImageContent]);

  const fullWidth = props.totalImageCount === props.imageIndex + 1 && props.totalImageCount % 2 === 1;

  return (
    <Grid item xs={fullWidth ? 12 : 6} className={cssExports.fileGridItemImage}>
      {imageContent && !isConverting ? (
        <img src={imageContent} onClick={props.onClick.bind(null, props.imageIndex ?? 0)} />
      ) : (
        <Skeleton className={cssExports.skeleton} />
      )}
    </Grid>
  );
};

export default FilesGridItemImage;
